import { useState } from 'react';
import { useAuth } from '../contexts/AuthContext';

/** Normalize phone input to E.164 format (+1XXXXXXXXXX) */
function normalizePhone(value) {
  const digits = value.replace(/\D/g, '');
  if (digits.length === 10) return `+1${digits}`;
  if (digits.length === 11 && digits.startsWith('1')) return `+${digits}`;
  return `+${digits}`;
}

/**
 * Shown after login when the user is missing either email or phone.
 * Collects the missing contact method and links it to their auth account.
 *
 * @param {{ type: 'email' | 'phone', onComplete: () => void }} props
 */
export default function CollectContact({ type, onComplete }) {
  const [value, setValue] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const { linkEmail, linkPhone } = useAuth();

  const isPhone = type === 'phone';

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    const trimmed = value.trim();

    // Basic validation
    if (isPhone) {
      const digits = trimmed.replace(/\D/g, '');
      if (digits.length < 10) {
        setError('Please enter a valid phone number.');
        setLoading(false);
        return;
      }
    } else {
      if (!trimmed.includes('@') || trimmed.length < 5) {
        setError('Please enter a valid email address.');
        setLoading(false);
        return;
      }
    }

    try {
      if (isPhone) {
        await linkPhone(normalizePhone(trimmed));
      } else {
        await linkEmail(trimmed.toLowerCase());
      }
      onComplete();
    } catch (err) {
      setError(err.message || 'Something went wrong. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div style={containerStyle}>
      <div style={cardStyle}>
        <div style={{ fontSize: '3.5rem', marginBottom: 16, textAlign: 'center' }}>
          {isPhone ? '📱' : '✉️'}
        </div>
        <h2 style={{
          fontSize: '1.5rem',
          fontWeight: 700,
          color: 'var(--text-primary)',
          marginBottom: 12,
          textAlign: 'center',
        }}>
          One more thing...
        </h2>
        <p style={{
          color: 'var(--text-secondary)',
          fontSize: '0.9375rem',
          textAlign: 'center',
          marginBottom: 24,
          lineHeight: 1.5,
        }}>
          {isPhone
            ? 'Add your phone number so you can sign in with either your phone or email.'
            : 'Add your email so you can sign in with either your email or phone.'
          }
        </p>

        {error && (
          <div style={{
            background: 'rgba(211, 71, 74, 0.1)',
            border: '1px solid rgba(211, 71, 74, 0.3)',
            borderRadius: 'var(--radius-sm)',
            padding: '10px 14px',
            marginBottom: 16,
            fontSize: '0.8125rem',
            color: 'var(--accent)',
          }}>
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit}>
          <div style={{ marginBottom: 16 }}>
            <input
              type={isPhone ? 'tel' : 'email'}
              inputMode={isPhone ? 'tel' : 'email'}
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder={isPhone ? '(555) 555-1234' : 'you@email.com'}
              required
              autoFocus
              className="form-input"
              style={{
                width: '100%',
                padding: '12px 14px',
                fontSize: '0.9375rem',
                boxSizing: 'border-box',
              }}
            />
          </div>

          <button
            type="submit"
            disabled={loading || !value.trim()}
            style={{
              width: '100%',
              padding: '14px 16px',
              fontSize: '0.9375rem',
              fontWeight: 600,
              borderRadius: 'var(--radius-sm)',
              border: 'none',
              background: 'var(--accent)',
              color: '#fff',
              cursor: (loading || !value.trim()) ? 'not-allowed' : 'pointer',
              opacity: (loading || !value.trim()) ? 0.6 : 1,
              transition: 'opacity 0.2s',
            }}
          >
            {loading ? 'Saving...' : 'Continue'}
          </button>
        </form>
      </div>
    </div>
  );
}

const containerStyle = {
  minHeight: '100dvh',
  width: '100%',
  background: 'var(--bg-primary)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: 16,
};

const cardStyle = {
  maxWidth: 400,
  width: '100%',
  background: 'var(--bg-elevated)',
  borderRadius: 'var(--radius-md)',
  padding: 32,
};
